import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/ui/dialog';
import { Button } from '@/ui/Button';
import { Input } from '@/ui/input';
import { Badge } from '@/ui/badge';
import { Plus, Search, Filter } from 'lucide-react';
import { supabaseService, type CustomRule, type GetRulesRequest } from '@/shared/services/supabaseService';
import { cn } from '@/lib/utils';

interface RuleSelectorProps {
  onSelect: (rule: CustomRule) => void;
  excludeIds?: string[];
  trigger?: React.ReactNode;
}

const RuleSelector: React.FC<RuleSelectorProps> = ({
  onSelect,
  excludeIds = [],
  trigger
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [rules, setRules] = useState<CustomRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPublicOnly, setShowPublicOnly] = useState(false);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const filters: GetRulesRequest = {
        search: searchTerm || undefined,
        limit: 50
      };

      let response;
      if (showPublicOnly) {
        response = await supabaseService.getPublicRules(filters);
      } else {
        response = await supabaseService.getCustomRules(filters);
      }

      const filteredRules = response.rules.filter(rule => !excludeIds.includes(rule.id));
      setRules(filteredRules);
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, showPublicOnly, excludeIds]);

  useEffect(() => {
    if (isOpen) {
      fetchRules();
    }
  }, [isOpen, fetchRules]);

  const handleSelect = (rule: CustomRule) => {
    onSelect(rule);
    setIsOpen(false);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="rounded-none font-mono uppercase text-xs border-neutral-950 dark:border-neutral-100">
      <Plus className="h-3 w-3 mr-2" />
      ADD_EXISTING_RULE
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || defaultTrigger}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col p-0 gap-0 border-2 border-neutral-950 dark:border-neutral-100 rounded-none bg-background">
        <DialogHeader className="p-6 border-b border-neutral-950 dark:border-neutral-100 bg-neutral-50 dark:bg-neutral-900/10">
          <DialogTitle className="font-mono text-xl uppercase font-bold tracking-tight">Select a Rule</DialogTitle>
          <DialogDescription className="font-mono text-xs text-muted-foreground mt-2">
            Choose from your custom rules or browse public rules to add to this repository.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Filters */}
          <div className="p-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center gap-4 bg-background">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="SEARCH_RULES..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 rounded-none font-mono text-xs border-neutral-300 dark:border-neutral-700 focus-visible:ring-0 focus-visible:border-neutral-950 dark:focus-visible:border-neutral-100"
              />
            </div>

            <Button
              variant={showPublicOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setShowPublicOnly(!showPublicOnly)}
              className={cn(
                  "rounded-none font-mono uppercase text-xs border-neutral-300 dark:border-neutral-700",
                  showPublicOnly && "bg-neutral-950 text-white dark:bg-white dark:text-black"
              )}
            >
              <Filter className="h-3 w-3 mr-2" />
              {showPublicOnly ? 'PUBLIC_ONLY' : 'ALL_RULES'}
            </Button>
          </div>

          {/* Rules List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-neutral-50/50 dark:bg-neutral-900/5">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="font-mono text-xs text-muted-foreground animate-pulse">LOADING_RULES...</div>
              </div>
            ) : rules.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="font-mono text-sm font-bold uppercase text-muted-foreground mb-2">NO_RULES_FOUND</div>
                  <div className="font-mono text-xs text-muted-foreground">
                    {searchTerm
                      ? 'Try adjusting your search criteria'
                      : 'Create your first rule to get started'
                    }
                  </div>
                </div>
              </div>
            ) : (
              rules.map(rule => (
                <div key={rule.id} className="border border-neutral-200 dark:border-neutral-800 bg-background p-4 hover:border-neutral-400 dark:hover:border-neutral-600 transition-colors group">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-mono font-bold text-sm uppercase truncate">{rule.title}</h3>
                        {rule.is_public && (
                          <Badge variant="secondary" className="rounded-none text-[10px] px-1 font-mono uppercase h-4">
                            Public
                          </Badge>
                        )}
                      </div>
                      {rule.description && (
                        <p className="font-mono text-xs text-muted-foreground mb-2 truncate">
                          {rule.description}
                        </p>
                      )}
                      <p className="font-mono text-xs text-muted-foreground line-clamp-2 bg-neutral-100 dark:bg-neutral-900 p-2 border border-neutral-100 dark:border-neutral-800 group-hover:border-neutral-300 dark:group-hover:border-neutral-700 transition-colors">
                        {rule.rule_content}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleSelect(rule)}
                      className="rounded-none font-mono uppercase text-xs h-8 bg-white dark:bg-black border border-neutral-950 dark:border-neutral-100 text-foreground hover:bg-neutral-100 dark:hover:bg-neutral-900 shadow-sm"
                    >
                      [ SELECT ]
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RuleSelector;
